import { getGatewayBaseUrl } from "../config";
import { PosLocalProjectionGatewayClient } from "../gateway/local-projection-client";
import { PosStockTransferGatewayClient, type StockTransferSnapshot } from "../gateway/stock-transfer-client";
import { applyStockRuntime } from "./indexed-runtime";
import { readVersionedLocalProjectionCursor } from "./local-projection-version";
import { applyStockMovementRuntime } from "./stock-movement-runtime";
import { applyStockTransferRuntime } from "./stock-transfer-runtime";
import type { TrustedTerminalIdentity } from "./terminal-state";

type StockTransferCommitScope = {
  identity: TrustedTerminalIdentity;
  deviceSecret: string;
  sessionToken: string;
};

function requireOnlineStockTransfer() {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    throw new Error("STOCK_TRANSFER_ONLINE_REQUIRED");
  }
}

async function readBackStockTransfer(scope: StockTransferCommitScope, baseUrl: string) {
  let readbackOk = true;
  try {
    const auth = { deviceSecret: scope.deviceSecret, sessionToken: scope.sessionToken };
    const transferClient = new PosStockTransferGatewayClient({ baseUrl });
    const projectionClient = new PosLocalProjectionGatewayClient({ baseUrl });

    const transfer = await transferClient.loadRuntime({ ...auth, limit: 500 });
    if (!transfer.ok || !transfer.data) throw new Error(transfer.error ?? "STOCK_TRANSFER_READBACK_FAILED");
    await applyStockTransferRuntime(scope.identity, transfer.data);

    const stockCursor = await readVersionedLocalProjectionCursor(scope.identity, "STOCK");
    const stock = await projectionClient.load({ ...auth, domain: "STOCK", cursor: stockCursor, limit: 500 });
    if (!stock.ok || !stock.data) throw new Error(stock.error ?? "STOCK_READBACK_FAILED");
    await applyStockRuntime(scope.identity, stock.data);

    const historyCursor = await readVersionedLocalProjectionCursor(scope.identity, "STOCK_HISTORY");
    const history = await projectionClient.load({ ...auth, domain: "STOCK_HISTORY", cursor: historyCursor, limit: 500 });
    if (!history.ok || !history.data) throw new Error(history.error ?? "STOCK_HISTORY_READBACK_FAILED");
    await applyStockMovementRuntime(scope.identity, history.data);
  } catch {
    // The Owner mutation already succeeded. Never replay the business command only because
    // readback transport failed; background Local Projection sync is the repair path.
    readbackOk = false;
  }
  return readbackOk;
}

export async function commitStockTransferDispatch(input: StockTransferCommitScope & {
  destinationBranchId: string;
  lines: Array<{ stockItemId: string; quantity: number }>;
  note: string | null;
  requestKey: string;
}) {
  requireOnlineStockTransfer();
  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) throw new Error("POS_GATEWAY_NOT_CONFIGURED");

  const client = new PosStockTransferGatewayClient({ baseUrl });
  const committed = await client.dispatch({
    deviceSecret: input.deviceSecret,
    sessionToken: input.sessionToken,
    destinationBranchId: input.destinationBranchId,
    lines: input.lines,
    note: input.note,
    requestKey: input.requestKey,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: input.identity.node_id,
      local_stock_transfer_authority: false,
      transfer_action: "DISPATCH",
    },
  });
  if (!committed.ok || !committed.data) {
    throw new Error(committed.error ?? "POS_STOCK_TRANSFER_DISPATCH_FAILED");
  }

  return {
    data: committed.data as StockTransferSnapshot,
    readbackOk: await readBackStockTransfer(input, baseUrl),
  };
}

export async function commitStockTransferReceive(input: StockTransferCommitScope & {
  transferId: string;
  lines: Array<{ stockItemId: string; receivedQuantity: number }>;
  note: string | null;
  requestKey: string;
}) {
  requireOnlineStockTransfer();
  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) throw new Error("POS_GATEWAY_NOT_CONFIGURED");

  const client = new PosStockTransferGatewayClient({ baseUrl });
  const committed = await client.receive({
    deviceSecret: input.deviceSecret,
    sessionToken: input.sessionToken,
    transferId: input.transferId,
    lines: input.lines,
    note: input.note,
    requestKey: input.requestKey,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: input.identity.node_id,
      local_stock_transfer_authority: false,
      transfer_action: "RECEIVE",
    },
  });
  if (!committed.ok || !committed.data) {
    throw new Error(committed.error ?? "POS_STOCK_TRANSFER_RECEIVE_FAILED");
  }

  return {
    data: committed.data as StockTransferSnapshot,
    readbackOk: await readBackStockTransfer(input, baseUrl),
  };
}
